import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { stored } from '../../../constants';
import { SessionType } from '../../../types';
import { areReviewDraftsLocked, isDraftExpired } from '../../../utils/sessionData';
import { formatSessionDate } from './sessionDisplay';

export const MAX_NOTE_LENGTH = 4000;
const EXPIRED_MESSAGE = 'Drafts are kept for 24 hours. This draft has expired and is no longer saved, so finish and submit before closing.';

export type ReviewDraft = {
    sessionNotes: Record<string, string>;
    overallNote: string;
    decision: string;
    finalNote: string | null;
    finalSource: string;
    uncertainSubmission: boolean;
};

const emptyDraft = (): ReviewDraft => ({
    sessionNotes: {}, overallNote: '', decision: '', finalNote: null,
    finalSource: '', uncertainSubmission: false,
});

type DraftStore = {
    key: string;
    draft: ReviewDraft;
    raw: string | null;
    dirty: boolean;
    blocked: boolean;
    error: string | null;
    savedAt: Date | null;
    createdAt: number | null;
};

const readDraft = (key: string): DraftStore => {
    const store: DraftStore = {
        key, draft: emptyDraft(), raw: null, dirty: false, blocked: false, error: null, savedAt: null, createdAt: null,
    };
    try {
        store.raw = window.localStorage.getItem(key);
        if (!store.raw) return store;
        const parsed: unknown = JSON.parse(store.raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Invalid draft');
        const value = parsed as Record<string, unknown>;
        if (value.createdAt !== undefined && typeof value.createdAt !== 'number') throw new Error('Invalid creation time');
        if (typeof value.createdAt === 'number' && isDraftExpired(value.createdAt)) {
            window.localStorage.removeItem(key);
            store.raw = null;
            return store;
        }
        store.createdAt = typeof value.createdAt === 'number' ? value.createdAt : null;
        if (value.version !== undefined && value.version !== 2) throw new Error('Unknown draft version');
        if (!value.sessionNotes || typeof value.sessionNotes !== 'object' || Array.isArray(value.sessionNotes)) {
            throw new Error('Invalid notes');
        }
        if (Object.entries(value.sessionNotes).some(([id, note]) => !/^\d+$/.test(id) || typeof note !== 'string')) {
            throw new Error('Invalid note');
        }
        const draft = emptyDraft();
        draft.sessionNotes = value.sessionNotes as Record<string, string>;
        for (const field of ['overallNote', 'decision', 'finalSource'] as const) {
            if (value[field] !== undefined && typeof value[field] !== 'string') throw new Error('Invalid text');
            draft[field] = (value[field] as string | undefined) ?? '';
        }
        if (value.finalNote !== undefined && value.finalNote !== null && typeof value.finalNote !== 'string') {
            throw new Error('Invalid final note');
        }
        if (value.uncertainSubmission !== undefined && typeof value.uncertainSubmission !== 'boolean') {
            throw new Error('Invalid submission state');
        }
        draft.finalNote = (value.finalNote as string | null | undefined) ?? null;
        draft.uncertainSubmission = value.uncertainSubmission === true;
        store.draft = draft;
    } catch {
        store.blocked = true;
        store.error = 'The saved draft could not be opened. It has not been changed. Contact support before discarding it.';
    }
    return store;
};

const persist = (store: DraftStore): boolean => {
    if (store.blocked || areReviewDraftsLocked()) return false;
    if (!store.dirty) return true;
    if (store.createdAt !== null && isDraftExpired(store.createdAt)) {
        store.error = EXPIRED_MESSAGE;
        return false;
    }
    try {
        if (window.localStorage.getItem(store.key) !== store.raw) {
            store.error = 'This draft changed in another tab. Keep this window open to avoid losing your unsaved edits.';
            return false;
        }
        const createdAt = store.createdAt ?? Date.now();
        const raw = JSON.stringify({ version: 2, createdAt, ...store.draft });
        window.localStorage.setItem(store.key, raw);
        store.createdAt = createdAt;
        store.raw = raw;
        store.dirty = false;
        store.error = null;
        store.savedAt = new Date();
        return true;
    } catch {
        store.error = 'Draft could not be saved on this browser. Keep this window open and retry saving before closing or reloading.';
        return false;
    }
};

export const compileReviewNote = (draft: ReviewDraft, sessions: Pick<SessionType, 'id' | 'date'>[]): string => {
    const blocks = sessions.flatMap((session, index) => {
        const note = draft.sessionNotes[session.id]?.trim();
        return note ? [`Session ${index + 1} of ${sessions.length} - ${formatSessionDate(session.date)} (#${session.id})\n${note}`] : [];
    });
    const knownIds = new Set(sessions.map(session => String(session.id)));
    Object.entries(draft.sessionNotes).forEach(([id, note]) => {
        if (!knownIds.has(id) && note.trim()) blocks.push(`Session #${id} (not in current session list)\n${note.trim()}`);
    });
    if (draft.overallNote.trim()) blocks.push(`Overall\n${draft.overallNote.trim()}`);
    return blocks.join('\n\n');
};

export const useReviewDraft = (reviewerId: number, caseId: number) => {
    const key = `${stored.reviewDraftPrefix}:${reviewerId}:${caseId}`;
    const store = useMemo(() => readDraft(key), [key]);
    const [, render] = useReducer((revision: number) => revision + 1, 0);
    const flushDraft = useCallback(() => {
        const saved = persist(store);
        render();
        return saved;
    }, [store]);
    const updateDraft = useCallback((changes: Partial<ReviewDraft>) => {
        store.draft = { ...store.draft, ...changes };
        store.dirty = true;
        render();
    }, [store]);
    const setSessionNote = useCallback((sessionId: number, note: string) => {
        updateDraft({ sessionNotes: { ...store.draft.sessionNotes, [sessionId]: note.slice(0, MAX_NOTE_LENGTH) } });
    }, [store, updateDraft]);

    useEffect(() => {
        if (!store.dirty) return;
        const timer = window.setTimeout(flushDraft, 400);
        return () => window.clearTimeout(timer);
    }, [store, store.draft, flushDraft]);

    useEffect(() => {
        const hide = () => { persist(store); };
        const visibility = () => { if (document.visibilityState === 'hidden') flushDraft(); };
        const unload = (event: BeforeUnloadEvent) => {
            if (!persist(store) && !areReviewDraftsLocked()) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('pagehide', hide);
        window.addEventListener('beforeunload', unload);
        document.addEventListener('visibilitychange', visibility);
        return () => {
            persist(store);
            window.removeEventListener('pagehide', hide);
            window.removeEventListener('beforeunload', unload);
            document.removeEventListener('visibilitychange', visibility);
        };
    }, [store, flushDraft]);

    const discardDraft = useCallback(() => {
        try {
            if (window.localStorage.getItem(key) !== store.raw) throw new Error('Draft changed');
            window.localStorage.removeItem(key);
            store.draft = emptyDraft();
            store.raw = null;
            store.dirty = false;
            store.blocked = false;
            store.error = null;
            store.savedAt = null;
            store.createdAt = null;
            render();
            return true;
        } catch {
            store.error = 'The draft could not be removed, or changed in another tab. Keep this window open to avoid losing your unsaved edits.';
            render();
            return false;
        }
    }, [key, store]);

    return {
        draft: store.draft, savedAt: store.savedAt, saveError: store.error,
        saveStatus: store.error ? 'Save failed' : store.dirty ? 'Saving...' : store.raw ? 'Saved on this browser' : 'No saved draft',
        setSessionNote, updateDraft, flushDraft, discardDraft,
        notedSessionIds: Object.entries(store.draft.sessionNotes).filter(([, note]) => note.trim()).map(([id]) => Number(id)),
    };
};
